import { PageHeader } from "@/components/ui/page";
import React, { useState, useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Edit, Trash2, Search, Receipt, Hash, BarChart3, Tag, Pencil, CreditCard } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { PermissionGate } from "@/features/auth/components/PermissionGate";
import { usePermission } from "@/hooks/usePermission";
import { expensesAPI, getApiErrorPayload } from "@/lib/api";
import { formatDate, cn } from "@/lib/utils";
import { Price } from "@/components/ui/price";
import { useCurrency } from "@/hooks/useCurrency";
import { Loading } from "@/components/ui/loader";
import { confirmAction } from "@/components/ui/confirm-dialog";
import { Pagination } from "@/components/ui/pagination";
import { EmptyState } from "@/components/ui/empty-state";
import { FieldError } from "@/components/ui/field-error";

interface Expense {
	id: number;
	description: string;
	amount: number;
	expenseDate: string;
	reference?: string;
	notes?: string;
	paymentMethod: string;
	category?: {
		id: number;
		name: string;
	};
	categoryId?: number;
	createdBy?: {
		id: number;
		firstName: string;
		lastName: string;
	};
}

interface ExpenseCategory {
	id: number;
	name: string;
	description?: string;
	isActive: boolean;
}

interface ExpenseStatistics {
	summary: { totalExpenses: number; totalCount: number };
	byCategory: { categoryId: number; categoryName: string; totalAmount: string | number; count: string | number }[];
}

interface ExpenseForm {
	description: string;
	amount: number;
	expenseDate: string;
	categoryId: string;
	paymentMethod: string;
	reference: string;
	notes: string;
}

const emptyExpenseForm = (): ExpenseForm => ({
	description: "",
	amount: 0,
	expenseDate: new Date().toISOString().split("T")[0],
	categoryId: "",
	paymentMethod: "CASH",
	reference: "",
	notes: "",
});

/**
 * Expenses and expense categories merged into one page:
 * category chips with inline CRUD over a single table,
 * with add/edit as one dialog (replaces the old detail/edit pages).
 */
export default function ExpensesPage() {
	const { t } = useTranslation();
	const { toast } = useToast();
	const currency = useCurrency();
	const [searchParams, setSearchParams] = useSearchParams();

	const [expenses, setExpenses] = useState<Expense[]>([]);
	const [categories, setCategories] = useState<ExpenseCategory[]>([]);
	const [loading, setLoading] = useState(true);
	// Full-page loader only on first load, so the search box keeps focus while results refresh.
	const [loadedOnce, setLoadedOnce] = useState(false);
	const [totalCount, setTotalCount] = useState(0);
	const [selectedCategory, setSelectedCategory] = useState<string>("");
	const [searchTerm, setSearchTerm] = useState("");
	const [debouncedSearch, setDebouncedSearch] = useState("");
	const [startDate, setStartDate] = useState<string>("");
	const [endDate, setEndDate] = useState<string>("");
	const [page, setPage] = useState(1);
	const [pageSize, setPageSize] = useState(20);
	// Totals come from the server, not from the rows on the current page.
	const [statistics, setStatistics] = useState<ExpenseStatistics | null>(null);
	// Filter changes can overlap in-flight requests; only the latest response is applied.
	const expensesRequest = useRef(0);
	const totalsRequest = useRef(0);

	const canManageCategories = usePermission(["expenses.create", "expenses.update", "expenses.delete"]);

	// Expense dialog (create + edit)
	const [expenseDialogOpen, setExpenseDialogOpen] = useState(false);
	const [editingExpenseId, setEditingExpenseId] = useState<number | null>(null);
	const [expenseForm, setExpenseForm] = useState<ExpenseForm>(emptyExpenseForm());
	const [savingExpense, setSavingExpense] = useState(false);
	const [expenseErrors, setExpenseErrors] = useState<{ description?: string; amount?: string; categoryId?: string }>(
		{},
	);
	const canCreateExpense = usePermission("expenses.create");

	// Category dialog (create + edit)
	const [categoryDialogMode, setCategoryDialogMode] = useState<"create" | "edit" | null>(null);
	const [editingCategory, setEditingCategory] = useState<ExpenseCategory | null>(null);
	const [categoryForm, setCategoryForm] = useState({ name: "", description: "" });
	const [categoryNameError, setCategoryNameError] = useState<string | null>(null);
	const [savingCategory, setSavingCategory] = useState(false);

	const paymentMethods = [
		{ value: "CASH", label: t("expenses.paymentMethods.cash") },
		{ value: "BANK_TRANSFER", label: t("expenses.paymentMethods.bankTransfer") },
		{ value: "CREDIT_CARD", label: t("expenses.paymentMethods.creditCard") },
		{ value: "CHECK", label: t("expenses.paymentMethods.check") },
		{ value: "ELECTRONIC_WALLET", label: t("expenses.paymentMethods.electronicWallet") },
	];

	useEffect(() => {
		const timer = window.setTimeout(() => setDebouncedSearch(searchTerm.trim()), 300);
		return () => window.clearTimeout(timer);
	}, [searchTerm]);

	useEffect(() => {
		setPage(1);
	}, [selectedCategory, startDate, endDate, debouncedSearch]);

	useEffect(() => {
		fetchExpenses();
		// eslint-disable-next-line react-hooks/exhaustive-deps -- fetch when filters or page change
	}, [selectedCategory, startDate, endDate, debouncedSearch, page, pageSize]);

	useEffect(() => {
		fetchTotals();
		// eslint-disable-next-line react-hooks/exhaustive-deps -- fetch when filters change
	}, [startDate, endDate, debouncedSearch]);

	useEffect(() => {
		fetchCategories();
		// eslint-disable-next-line react-hooks/exhaustive-deps -- mount-only fetch
	}, []);

	const fetchExpenses = async () => {
		const requestId = ++expensesRequest.current;
		try {
			setLoading(true);
			const response = await expensesAPI.getAll(
				page,
				pageSize,
				selectedCategory || undefined,
				startDate,
				endDate,
				debouncedSearch,
			);
			if (requestId !== expensesRequest.current) return;
			const rows: Expense[] = response.expenses || [];
			// The last row of the last page was deleted: step back one page.
			if (rows.length === 0 && page > 1) {
				setPage(page - 1);
				return;
			}
			setExpenses(rows);
			setTotalCount(response.total || 0);
		} catch (error) {
			if (requestId !== expensesRequest.current) return;
			toast({
				title: t("common.error"),
				description: t("expenses.errors.fetchFailed"),
				variant: "destructive",
			});
		} finally {
			if (requestId === expensesRequest.current) {
				setLoading(false);
				setLoadedOnce(true);
			}
		}
	};

	// Not filtered by category, so every chip keeps its count; the selected one is read from byCategory.
	const fetchTotals = async () => {
		const requestId = ++totalsRequest.current;
		try {
			const stats: ExpenseStatistics = await expensesAPI.getStatistics({
				startDate: startDate || undefined,
				endDate: endDate || undefined,
				search: debouncedSearch || undefined,
			});
			if (requestId === totalsRequest.current) setStatistics(stats);
		} catch (error) {
			if (requestId === totalsRequest.current) setStatistics(null);
		}
	};

	const refreshExpenses = () => {
		fetchExpenses();
		fetchTotals();
	};

	const fetchCategories = async () => {
		try {
			const response = await expensesAPI.getAllCategories();
			setCategories(Array.isArray(response) ? response : []);
		} catch (error) {
			toast({
				title: t("common.error"),
				description: t("expenses.errors.categoriesFetchFailed"),
				variant: "destructive",
			});
		}
	};

	/* ===== expense dialog ===== */

	const openCreateExpense = () => {
		setExpenseErrors({});
		setEditingExpenseId(null);
		setExpenseForm(emptyExpenseForm());
		setExpenseDialogOpen(true);
	};

	const openEditExpense = (expense: Expense) => {
		setExpenseErrors({});
		setEditingExpenseId(expense.id);
		setExpenseForm({
			description: expense.description || "",
			amount: Number(expense.amount) || 0,
			expenseDate: (expense.expenseDate || "").split("T")[0] || new Date().toISOString().split("T")[0],
			categoryId: String(expense.category?.id ?? expense.categoryId ?? ""),
			paymentMethod: expense.paymentMethod || "CASH",
			reference: expense.reference || "",
			notes: expense.notes || "",
		});
		setExpenseDialogOpen(true);
	};

	// Deep links kept from the old detail/edit pages: ?edit=<id> and ?new=1
	useEffect(() => {
		const editId = searchParams.get("edit");
		const isNew = searchParams.get("new") === "1";
		if (editId) {
			expensesAPI
				.getById(editId)
				.then((data: Expense) => openEditExpense(data))
				.catch(() => undefined);
		} else if (isNew) {
			openCreateExpense();
		}
		if (editId || isNew) {
			setSearchParams(
				prev => {
					const next = new URLSearchParams(prev);
					next.delete("edit");
					next.delete("new");
					return next;
				},
				{ replace: true },
			);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps -- run once for deep links
	}, []);

	const handleSaveExpense = async () => {
		const errors: { description?: string; amount?: string; categoryId?: string } = {};
		if (!expenseForm.description.trim()) errors.description = "expenses.errors.descriptionRequired";
		if (!(expenseForm.amount > 0)) errors.amount = "expenses.errors.amountRequired";
		if (!expenseForm.categoryId) errors.categoryId = "expenses.errors.categoryRequired";
		setExpenseErrors(errors);
		if (Object.keys(errors).length > 0 || savingExpense) return;
		const payload = {
			description: expenseForm.description.trim(),
			amount: Number(expenseForm.amount),
			expenseDate: expenseForm.expenseDate,
			categoryId: Number(expenseForm.categoryId),
			paymentMethod: expenseForm.paymentMethod,
			reference: expenseForm.reference.trim() || undefined,
			notes: expenseForm.notes.trim() || undefined,
		};
		try {
			setSavingExpense(true);
			if (editingExpenseId) {
				await expensesAPI.update(editingExpenseId, payload);
				toast({ title: t("common.success"), description: t("expenses.messages.updated") });
			} else {
				await expensesAPI.create(payload);
				toast({ title: t("common.success"), description: t("expenses.messages.created") });
			}
			setExpenseDialogOpen(false);
			refreshExpenses();
		} catch (error) {
			const apiPayload = getApiErrorPayload(error);
			const errorMessage = t(apiPayload.key, (apiPayload.params ?? {}) as Record<string, string>);
			toast({
				title: t("common.error"),
				description:
					errorMessage === apiPayload.key
						? t(editingExpenseId ? "expenses.errors.updateFailed" : "expenses.errors.createFailed")
						: errorMessage,
				variant: "destructive",
			});
		} finally {
			setSavingExpense(false);
		}
	};

	const handleDeleteExpense = async (id: number) => {
		if (!(await confirmAction(t("expenses.confirmations.delete"), { confirmLabel: t("common.delete") }))) return;
		try {
			await expensesAPI.remove(id);
			toast({ title: t("common.success"), description: t("expenses.messages.deleted") });
			refreshExpenses();
		} catch (error) {
			toast({
				title: t("common.error"),
				description: t("expenses.errors.deleteFailed"),
				variant: "destructive",
			});
		}
	};

	/* ===== category dialog + chips ===== */

	const openCreateCategory = () => {
		setCategoryNameError(null);
		setEditingCategory(null);
		setCategoryForm({ name: "", description: "" });
		setCategoryDialogMode("create");
	};

	const openEditCategory = (category: ExpenseCategory) => {
		setCategoryNameError(null);
		setEditingCategory(category);
		setCategoryForm({ name: category.name, description: category.description || "" });
		setCategoryDialogMode("edit");
	};

	const handleSaveCategory = async () => {
		if (!categoryForm.name.trim()) {
			setCategoryNameError("expenses.errors.nameRequired");
			return;
		}
		if (savingCategory) return;
		try {
			setSavingCategory(true);
			if (categoryDialogMode === "edit" && editingCategory) {
				await expensesAPI.updateCategory(editingCategory.id, categoryForm);
				toast({ title: t("common.success"), description: t("expenses.messages.categoryUpdated") });
			} else {
				await expensesAPI.createCategory(categoryForm);
				toast({ title: t("common.success"), description: t("expenses.messages.categoryCreated") });
			}
			setCategoryDialogMode(null);
			fetchCategories();
		} catch (error) {
			toast({
				title: t("common.error"),
				description: t(
					categoryDialogMode === "edit"
						? "expenses.errors.categoryUpdateFailed"
						: "expenses.errors.categoryCreateFailed",
				),
				variant: "destructive",
			});
		} finally {
			setSavingCategory(false);
		}
	};

	const handleDeleteCategory = async (category: ExpenseCategory) => {
		if (!(await confirmAction(t("expenses.confirmations.deleteCategory"), { confirmLabel: t("common.delete") })))
			return;
		try {
			await expensesAPI.removeCategory(category.id);
			toast({ title: t("common.success"), description: t("expenses.messages.categoryDeleted") });
			if (String(selectedCategory) === String(category.id)) setSelectedCategory("");
			fetchCategories();
		} catch (error) {
			toast({
				title: t("common.error"),
				description: t("expenses.errors.categoryDeleteFailed"),
				variant: "destructive",
			});
		}
	};

	/* ===== derived data ===== */

	const byCategory = statistics?.byCategory ?? [];
	const categoryStats = (categoryId: number | string) =>
		byCategory.find(c => String(c.categoryId) === String(categoryId));

	const totalAmount = !statistics
		? null
		: selectedCategory
			? Number(categoryStats(selectedCategory)?.totalAmount) || 0
			: statistics.summary.totalExpenses;

	const topCategory =
		!selectedCategory && byCategory.length
			? { name: byCategory[0].categoryName, amount: Number(byCategory[0].totalAmount) || 0 }
			: null;

	const categoryCount = (category: ExpenseCategory) => Number(categoryStats(category.id)?.count) || 0;

	if (!loadedOnce) return <Loading />;

	return (
		<div className="space-y-5 p-4 md:p-6">
			<PageHeader
				title={t("expenses.title")}
				actions={
					<PermissionGate permission="expenses.create">
						<Button className="flex items-center gap-2 shrink-0" onClick={openCreateExpense}>
							<Plus className="h-4 w-4" />
							{t("expenses.actions.addExpense")}
						</Button>
					</PermissionGate>
				}
			/>
			{/* Header: summary + add expense */}
			<div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
				<div className="flex flex-wrap gap-3">
					<div className="flex items-center gap-3 rounded-xl border bg-card px-4 py-2.5">
						<Receipt className="h-5 w-5 text-muted-foreground" />
						<div>
							<div className="text-lg font-bold leading-none">
								{totalAmount === null ? "—" : <Price value={totalAmount} currency={currency} />}
							</div>
							<div className="text-xs text-muted-foreground mt-1">
								{t("expenses.summary.totalExpenses")}
							</div>
						</div>
					</div>
					<div className="flex items-center gap-3 rounded-xl border bg-card px-4 py-2.5">
						<Hash className="h-5 w-5 text-muted-foreground" />
						<div>
							<div className="text-lg font-bold leading-none">{totalCount}</div>
							<div className="text-xs text-muted-foreground mt-1">{t("expenses.summary.totalCount")}</div>
						</div>
					</div>
					{topCategory && (
						<div className="flex items-center gap-3 rounded-xl border bg-card px-4 py-2.5">
							<BarChart3 className="h-5 w-5 text-muted-foreground" />
							<div>
								<div className="text-lg font-bold leading-none">{topCategory.name}</div>
								<div className="text-xs text-muted-foreground mt-1">
									{t("expenses.summary.topCategory")} (
									<Price value={topCategory.amount} currency={currency} />)
								</div>
							</div>
						</div>
					)}
				</div>
			</div>

			{/* Category chips (filter + inline management) */}
			<div className="space-y-2">
				<div className="flex items-center justify-between">
					<p className="text-sm font-medium flex items-center gap-2">
						<Tag className="h-4 w-4 text-muted-foreground" />
						{t("expenses.tabs.categories")}
					</p>
					<PermissionGate permission="expenses.create">
						<Button variant="outline" size="sm" onClick={openCreateCategory}>
							<Plus className="h-4 w-4 me-1" />
							{t("expenses.actions.addCategory")}
						</Button>
					</PermissionGate>
				</div>
				<div className="flex gap-2 overflow-x-auto pb-2">
					<button
						type="button"
						onClick={() => setSelectedCategory("")}
						className={cn(
							"shrink-0 flex items-center gap-2 rounded-full border px-4 py-2 text-sm transition-colors",
							selectedCategory === ""
								? "bg-primary/10 text-primary border-transparent font-semibold"
								: "bg-card text-muted-foreground hover:border-primary/50",
						)}
					>
						{t("common.all")}
					</button>
					{categories.map(category => {
						const isSelected = String(selectedCategory) === String(category.id);
						return (
							<div
								key={category.id}
								className={cn(
									"shrink-0 flex items-center gap-1.5 rounded-full border ps-4 pe-2 py-1.5 text-sm transition-colors",
									isSelected
										? "border-primary bg-secondary text-primary font-semibold"
										: "bg-card text-muted-foreground hover:border-primary/50",
									!category.isActive && "opacity-60",
								)}
							>
								<button
									type="button"
									className="flex items-center gap-2"
									onClick={() =>
										setSelectedCategory(prev =>
											String(prev) === String(category.id) ? "" : String(category.id),
										)
									}
								>
									<span>{category.name}</span>
									<span
										className={cn(
											"rounded-full px-2 py-0.5 text-xs",
											isSelected
												? "bg-primary/10 text-primary"
												: "bg-muted text-muted-foreground",
										)}
									>
										{categoryCount(category)}
									</span>
								</button>
								{canManageCategories && (
									<span className="flex items-center">
										<PermissionGate permission="expenses.update">
											<button
												type="button"
												className="rounded-full p-1 hover:bg-muted"
												onClick={() => openEditCategory(category)}
												title={t("common.edit")}
												aria-label={t("common.edit")}
											>
												<Pencil className="h-3 w-3" />
											</button>
										</PermissionGate>
										<PermissionGate permission="expenses.delete">
											<button
												type="button"
												className="rounded-full p-1 hover:bg-destructive/10 hover:text-destructive"
												onClick={() => handleDeleteCategory(category)}
												title={t("common.delete")}
												aria-label={t("common.delete")}
											>
												<Trash2 className="h-3 w-3" />
											</button>
										</PermissionGate>
									</span>
								)}
							</div>
						);
					})}
				</div>
			</div>

			{/* Search + date range */}
			<div className="flex flex-col gap-3 sm:flex-row sm:items-center">
				<div className="relative flex-1 sm:max-w-sm">
					<Search className="absolute start-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
					<Input
						placeholder={t("expenses.filters.search")}
						aria-label={t("expenses.filters.search")}
						value={searchTerm}
						onChange={e => setSearchTerm(e.target.value)}
						className="ps-10"
					/>
				</div>
				<Input
					type="date"
					value={startDate}
					onChange={e => setStartDate(e.target.value)}
					className="w-full sm:w-44"
					title={t("expenses.filters.startDate")}
					aria-label={t("expenses.filters.startDate")}
				/>
				<Input
					type="date"
					value={endDate}
					onChange={e => setEndDate(e.target.value)}
					className="w-full sm:w-44"
					title={t("expenses.filters.endDate")}
					aria-label={t("expenses.filters.endDate")}
				/>
				{(searchTerm || selectedCategory || startDate || endDate) && (
					<Button
						variant="link"
						className="text-destructive"
						onClick={() => {
							setSearchTerm("");
							setSelectedCategory("");
							setStartDate("");
							setEndDate("");
						}}
					>
						{t("products.filters.clearFilters")}
					</Button>
				)}
			</div>

			{/* Expenses table */}
			<Card>
				<CardContent className="pt-5">
					{loading ? (
						<Loading />
					) : expenses.length === 0 ? (
						searchTerm.trim() || selectedCategory || startDate || endDate ? (
							<EmptyState icon={Search} title={t("common.noResults")} />
						) : (
							<EmptyState
								icon={Receipt}
								title={t("expenses.empty.title")}
								action={
									canCreateExpense
										? { label: t("expenses.actions.addExpense"), onClick: openCreateExpense }
										: undefined
								}
							/>
						)
					) : (
						<div className="record-surface">
							<Table>
								<TableHeader>
									<TableRow>
										<TableHead>{t("expenses.table.description")}</TableHead>
										<TableHead className="text-center">{t("expenses.table.amount")}</TableHead>
										<TableHead className="text-center">{t("expenses.table.date")}</TableHead>
										<TableHead className="text-center">
											{t("expenses.table.paymentMethod")}
										</TableHead>
										<TableHead className="text-center">{t("expenses.table.createdBy")}</TableHead>
										<TableHead className="text-center w-28">{t("common.actions")}</TableHead>
									</TableRow>
								</TableHeader>
								<TableBody>
									{expenses.map(expense => (
										<TableRow key={expense.id}>
											<TableCell>
												<div className="font-medium">{expense.description}</div>
												<div className="mt-1 flex items-center gap-2">
													{expense.category && (
														<Badge variant="secondary">{expense.category.name}</Badge>
													)}
													{expense.reference && (
														<span className="text-xs text-muted-foreground">
															{expense.reference}
														</span>
													)}
												</div>
											</TableCell>
											<TableCell className="text-center font-bold">
												<Price value={expense.amount} currency={currency} />
											</TableCell>
											<TableCell className="text-center">
												{formatDate(expense.expenseDate)}
											</TableCell>
											<TableCell className="text-center">
												<Badge variant="outline">
													<CreditCard className="h-3 w-3 me-1" />
													{paymentMethods.find(m => m.value === expense.paymentMethod)
														?.label || expense.paymentMethod}
												</Badge>
											</TableCell>
											<TableCell className="text-center">
												{expense.createdBy
													? `${expense.createdBy.firstName} ${expense.createdBy.lastName}`
													: "-"}
											</TableCell>
											<TableCell className="text-center">
												<div className="flex gap-2 justify-center">
													<PermissionGate permission="expenses.update">
														<Button
															variant="outline"
															size="sm"
															onClick={() => openEditExpense(expense)}
															title={t("common.edit")}
															aria-label={t("common.edit")}
														>
															<Edit className="h-4 w-4 text-primary" />
														</Button>
													</PermissionGate>
													<PermissionGate permission="expenses.delete">
														<Button
															variant="outline"
															size="sm"
															onClick={() => handleDeleteExpense(expense.id)}
															title={t("common.delete")}
															aria-label={t("common.delete")}
														>
															<Trash2 className="h-4 w-4 text-destructive" />
														</Button>
													</PermissionGate>
												</div>
											</TableCell>
										</TableRow>
									))}
								</TableBody>
							</Table>
						</div>
					)}
					{totalCount > 0 && (
						<Pagination
							currentPage={page}
							totalPages={Math.ceil(totalCount / pageSize)}
							totalItems={totalCount}
							itemsPerPage={pageSize}
							onPageChange={setPage}
							onPageSizeChange={setPageSize}
							itemName="navigation.expenses"
						/>
					)}
				</CardContent>
			</Card>

			{/* Add / Edit expense dialog */}
			<Dialog open={expenseDialogOpen} onOpenChange={open => !open && setExpenseDialogOpen(false)}>
				<DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
					<DialogHeader>
						<DialogTitle>
							{editingExpenseId ? t("expenses.actions.editExpense") : t("expenses.actions.addExpense")}
						</DialogTitle>
						<DialogDescription>{t("expenses.dialogs.expenseDescription")}</DialogDescription>
					</DialogHeader>
					<div className="space-y-4">
						<div className="space-y-2">
							<Label htmlFor="expense-description">
								{t("expenses.fields.description")} <span className="text-destructive">*</span>
							</Label>
							<Input
								id="expense-description"
								value={expenseForm.description}
								onChange={e => {
									setExpenseForm({ ...expenseForm, description: e.target.value });
									setExpenseErrors(prev => ({ ...prev, description: undefined }));
								}}
								placeholder={t("expenses.placeholders.description")}
								aria-invalid={!!expenseErrors.description}
							/>
							<FieldError message={expenseErrors.description} />
						</div>
						<div className="grid grid-cols-2 gap-3">
							<div className="space-y-2">
								<Label htmlFor="expense-amount">
									{t("expenses.fields.amount")} <span className="text-destructive">*</span>
								</Label>
								<Input
									id="expense-amount"
									type="number"
									min="0"
									step="0.01"
									value={expenseForm.amount}
									onChange={e => {
										setExpenseForm({ ...expenseForm, amount: parseFloat(e.target.value) || 0 });
										setExpenseErrors(prev => ({ ...prev, amount: undefined }));
									}}
									aria-invalid={!!expenseErrors.amount}
								/>
								<FieldError message={expenseErrors.amount} />
							</div>
							<div className="space-y-2">
								<Label htmlFor="expense-date">{t("expenses.fields.expenseDate")}</Label>
								<Input
									id="expense-date"
									type="date"
									value={expenseForm.expenseDate}
									onChange={e => setExpenseForm({ ...expenseForm, expenseDate: e.target.value })}
								/>
							</div>
						</div>
						<div className="grid grid-cols-2 gap-3">
							<div className="space-y-2">
								<Label htmlFor="expense-category">
									{t("expenses.fields.category")} <span className="text-destructive">*</span>
								</Label>
								<Select
									value={expenseForm.categoryId}
									onValueChange={value => {
										setExpenseForm({ ...expenseForm, categoryId: value });
										setExpenseErrors(prev => ({ ...prev, categoryId: undefined }));
									}}
								>
									<SelectTrigger id="expense-category" aria-invalid={!!expenseErrors.categoryId}>
										<SelectValue placeholder={t("expenses.placeholders.selectCategory")} />
									</SelectTrigger>
									<SelectContent>
										{categories.map(category => (
											<SelectItem key={category.id} value={String(category.id)}>
												{category.name}
											</SelectItem>
										))}
									</SelectContent>
								</Select>
								<FieldError message={expenseErrors.categoryId} />
							</div>
							<div className="space-y-2">
								<Label>{t("expenses.fields.paymentMethod")}</Label>
								<Select
									value={expenseForm.paymentMethod}
									onValueChange={value => setExpenseForm({ ...expenseForm, paymentMethod: value })}
								>
									<SelectTrigger aria-label={t("expenses.fields.paymentMethod")}>
										<SelectValue />
									</SelectTrigger>
									<SelectContent>
										{paymentMethods.map(method => (
											<SelectItem key={method.value} value={method.value}>
												{method.label}
											</SelectItem>
										))}
									</SelectContent>
								</Select>
							</div>
						</div>
						<div className="space-y-2">
							<Label htmlFor="expense-reference">{t("expenses.fields.reference")}</Label>
							<Input
								id="expense-reference"
								value={expenseForm.reference}
								onChange={e => setExpenseForm({ ...expenseForm, reference: e.target.value })}
								placeholder={t("expenses.placeholders.reference")}
							/>
						</div>
						<div className="space-y-2">
							<Label htmlFor="expense-notes">{t("expenses.fields.notes")}</Label>
							<Textarea
								id="expense-notes"
								rows={2}
								value={expenseForm.notes}
								onChange={e => setExpenseForm({ ...expenseForm, notes: e.target.value })}
								placeholder={t("expenses.placeholders.notes")}
							/>
						</div>
					</div>
					<DialogFooter>
						<Button variant="outline" onClick={() => setExpenseDialogOpen(false)} disabled={savingExpense}>
							{t("common.cancel")}
						</Button>
						<Button onClick={handleSaveExpense} disabled={savingExpense}>
							{savingExpense ? (
								<div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary" />
							) : (
								<span>{t("common.save")}</span>
							)}
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>

			{/* Add / Edit category dialog */}
			<Dialog open={categoryDialogMode !== null} onOpenChange={open => !open && setCategoryDialogMode(null)}>
				<DialogContent>
					<DialogHeader>
						<DialogTitle>
							{categoryDialogMode === "edit"
								? t("expenses.actions.editCategory")
								: t("expenses.actions.addCategory")}
						</DialogTitle>
						<DialogDescription>
							{categoryDialogMode === "edit"
								? t("expenses.dialogs.editCategoryDescription")
								: t("expenses.dialogs.categoryDescription")}
						</DialogDescription>
					</DialogHeader>
					<div className="space-y-4">
						<div className="space-y-2">
							<Label htmlFor="category-name">
								{t("expenses.fields.name")} <span className="text-destructive">*</span>
							</Label>
							<Input
								id="category-name"
								value={categoryForm.name}
								onChange={e => {
									setCategoryForm({ ...categoryForm, name: e.target.value });
									setCategoryNameError(null);
								}}
								placeholder={t("expenses.placeholders.categoryName")}
								aria-invalid={!!categoryNameError}
							/>
							<FieldError message={categoryNameError} />
						</div>
						<div className="space-y-2">
							<Label htmlFor="category-description">{t("expenses.fields.description")}</Label>
							<Input
								id="category-description"
								value={categoryForm.description}
								onChange={e => setCategoryForm({ ...categoryForm, description: e.target.value })}
								placeholder={t("expenses.placeholders.categoryDescription")}
							/>
						</div>
					</div>
					<DialogFooter>
						<Button variant="outline" onClick={() => setCategoryDialogMode(null)} disabled={savingCategory}>
							{t("common.cancel")}
						</Button>
						<Button onClick={handleSaveCategory} disabled={savingCategory}>
							{savingCategory
								? t("common.saving")
								: categoryDialogMode === "edit"
									? t("common.save")
									: t("common.create")}
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>
		</div>
	);
}
